/*
 * Product-owned adaptation of @a2000/window 0.0.34.
 * Upstream source: https://github.com/andricokaroulla/a2k (MIT License).
 * The title bar, action, panel, stack, and style tokens intentionally retain
 * the upstream structure; positioning and pointer handling are product-owned.
 */
import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";

import "./a2k-button/lib/src/a2k-button.js";
import "./a2k-icon/a2k-icon";
import "./a2k-panel/lib/src/a2k-panel.js";
import "./a2k-stack/lib/src/a2k-stack.js";

class A2kWindowTopbar extends LitElement {
  static styles = css`
    #wrapper {
      background: var(--window-topbar-background);
      user-select: none;
      height: var(--window-topbar-height);
      display: flex;
      align-items: center;
    }

    #wrapper[data-state="inactive"] {
      background: var(--window-topbar-background-inactive);
    }

    h2 {
      margin: 0;
      min-width: 0;
      overflow: hidden;
      font-size: var(--window-topbar-heading-font-size);
      color: var(--window-topbar-heading-color);
      padding: 0 var(--window-spacing-horizontal);
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    #wrapper[data-state="inactive"] h2 {
      color: var(--window-topbar-heading-color-inactive);
    }
  `;

  @property({ attribute: "data-state" })
  dataState = "active";

  render() {
    return html`<div data-state=${this.dataState} id="wrapper">
      <h2 class="heading"><slot></slot></h2>
    </div>`;
  }
}

class A2kWindowActions extends LitElement {
  static styles = css`
    :host {
      display: block;
      flex: 0 0 auto;
      max-width: 32px;
      overflow: hidden;
    }

    #icon-wrapper {
      font-size: 8px;
    }
  `;

  @property({ type: Boolean }) closeable = false;

  private handleClose() {
    if (!this.closeable) return;
    this.dispatchEvent(
      new Event("close", { bubbles: true, composed: true, cancelable: true }),
    );
  }

  render() {
    return html`<a2k-button
      ?disabled=${!this.closeable}
      @click=${this.handleClose}
      size="small"
      aria-label="Close Window"
    >
      <div id="icon-wrapper"><a2k-icon icon="cross-icon"></a2k-icon></div>
    </a2k-button>`;
  }
}

if (!customElements.get("a2k-window-topbar")) {
  customElements.define("a2k-window-topbar", A2kWindowTopbar);
}
if (!customElements.get("a2k-window-actions")) {
  customElements.define("a2k-window-actions", A2kWindowActions);
}

type WindowLayout = "flow" | "desktop";
const booleanAttribute = {
  fromAttribute(value: string | null) {
    return value !== null && value !== "false";
  },
  toAttribute(value: boolean) {
    return value ? "" : null;
  },
};

@customElement("cpl-a2k-window")
export class A2kWindow extends LitElement {
  static styles = css`
    :host([hidden]) {
      display: none;
    }

    :host {
      display: block;
      width: var(--window-width);
      max-width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }

    ::slotted(*) {
      padding: 0 var(--window-spacing-horizontal);
    }

    ::slotted([slot="toolbar"]),
    ::slotted([slot="statusbar"]) {
      padding: 0;
    }

    #window {
      font-family: var(--font-primary);
      letter-spacing: var(--font-primary-letter-spacing);
      width: var(--window-width);
      max-width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }

    #window {
      display: flex;
      flex-direction: column;
      min-height: 0;
      max-height: var(--window-max-height, max(0px, calc(100vh - 100px)));
      max-height: var(--window-max-height, max(0px, calc(100dvh - 100px)));
      overflow: hidden;
      border: var(--panel-border);
      background-color: var(--panel-color-background);
    }

    #content {
      flex: 1 1 auto;
      min-height: 0;
      overflow-x: auto;
      overflow-y: auto;
    }

    #content::-webkit-scrollbar {
      width: 16px;
      height: 16px;
    }
    #content::-webkit-scrollbar-track {
      background: repeating-linear-gradient(
        45deg,
        #c0c0c0 0 1px,
        #e0e0e0 1px 2px
      );
    }
    #content::-webkit-scrollbar-thumb {
      min-height: 24px;
      background: #c0c0c0;
      border: 2px outset #fff;
      box-shadow: inset -1px -1px #808080;
    }
    #content::-webkit-scrollbar-button {
      display: none;
    }
    #content::-webkit-scrollbar-corner {
      background: #c0c0c0;
    }

    #content::-webkit-scrollbar-button:vertical:decrement:start {
      display: block;
      width: 16px;
      height: 16px;
      border: 2px outset #fff;
      background: #c0c0c0
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12'%3E%3Cpolygon points='3,7 6,3 9,7' fill='black'/%3E%3C/svg%3E")
        center no-repeat;
    }

    #content::-webkit-scrollbar-button:vertical:increment:end {
      display: block;
      width: 16px;
      height: 16px;
      border: 2px outset #fff;
      background: #c0c0c0
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12'%3E%3Cpolygon points='3,4 6,8 9,4' fill='black'/%3E%3C/svg%3E")
        center no-repeat;
    }

    #content::-webkit-scrollbar-button:horizontal:decrement:start {
      display: block;
      width: 16px;
      height: 16px;
      border: 2px outset #fff;
      background: #c0c0c0
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12'%3E%3Cpolygon points='7,3 3,6 7,9' fill='black'/%3E%3C/svg%3E")
        center no-repeat;
    }

    #content::-webkit-scrollbar-button:horizontal:increment:end {
      display: block;
      width: 16px;
      height: 16px;
      border: 2px outset #fff;
      background: #c0c0c0
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12'%3E%3Cpolygon points='4,3 8,6 4,9' fill='black'/%3E%3C/svg%3E")
        center no-repeat;
    }
    #topbar-wrapper,
    slot[name="toolbar"],
    slot[name="statusbar"] {
      flex: 0 0 auto;
    }

    slot[name="toolbar"],
    slot[name="statusbar"] {
      display: block;
      min-height: 0;
    }

    #window[data-layout="flow"] {
      position: relative;
    }

    #window[data-layout="desktop"] {
      position: absolute;
      touch-action: none;
    }

    #draggable {
      width: 100%;
      min-width: 0;
      position: relative;
    }

    #draggable[data-draggable="true"][data-dragging="idle"] {
      cursor: var(--cursor-grab);
    }

    #draggable[data-dragging="dragging"] {
      cursor: var(--cursor-grabbing);
    }

    #topbar-wrapper {
      display: flex;
      justify-content: space-between;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      position: relative;
      overflow: hidden;
      box-sizing: border-box;
    }

    #draggable {
      flex: 1 1 auto;
      width: auto;
      overflow: hidden;
      min-width: 0;
    }

    a2k-window-actions {
      flex: 0 0 32px;
      width: 32px;
      max-width: 32px;
      min-width: 32px;
      box-sizing: border-box;
      overflow: hidden;
    }
  `;

  @property() heading = "";
  @property({ type: Boolean }) active = true;
  @property({ converter: booleanAttribute }) draggable = false;
  @property({ type: Boolean }) closeable = false;
  @property() layout: WindowLayout = "flow";
  @property({ type: Number }) x = 0;
  @property({ type: Number }) y = 0;
  @state() private dragging = false;

  private pointerId = -1;
  private pointerX = 0;
  private pointerY = 0;
  private startX = 0;
  private startY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("close", this.handleCloseEvent);
  }

  disconnectedCallback() {
    this.endDrag();
    this.removeEventListener("close", this.handleCloseEvent);
    super.disconnectedCallback();
  }

  protected updated(changed: Map<PropertyKey, unknown>) {
    if (changed.has("draggable") && !this.draggable) this.endDrag();
  }

  private handleCloseEvent = (event: Event) => {
    if (event.target !== this) return;
    if (!event.defaultPrevented) this.remove();
  };

  private beginDrag = (event: PointerEvent) => {
    if (!this.draggable || this.layout !== "desktop") return;
    if (event.button !== 0) return;
    event.preventDefault();
    this.pointerId = event.pointerId;
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    this.startX = this.x;
    this.startY = this.y;
    this.dragging = true;
    window.addEventListener("pointermove", this.handlePointerMove);
    window.addEventListener("pointerup", this.handlePointerUp, { once: true });
    window.addEventListener("pointercancel", this.handlePointerUp, {
      once: true,
    });
  };

  private handlePointerMove = (event: PointerEvent) => {
    if (event.pointerId !== this.pointerId) return;
    const bounds = this.getDragBounds();
    this.x = this.clamp(
      this.startX + event.clientX - this.pointerX,
      bounds.minX,
      bounds.maxX,
    );
    this.y = this.clamp(
      this.startY + event.clientY - this.pointerY,
      bounds.minY,
      bounds.maxY,
    );
  };

  private handlePointerUp = (event: PointerEvent) => {
    if (event.pointerId !== this.pointerId) return;
    this.endDrag();
  };

  private endDrag() {
    if (!this.dragging && this.pointerId < 0) return;
    this.dragging = false;
    this.pointerId = -1;
    window.removeEventListener("pointermove", this.handlePointerMove);
    window.removeEventListener("pointerup", this.handlePointerUp);
    window.removeEventListener("pointercancel", this.handlePointerUp);
  }

  private clamp(value: number, minimum: number, maximum: number) {
    return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
  }

  private getDragBounds() {
    const windowElement = this.renderRoot.querySelector("#window");
    if (!(windowElement instanceof HTMLElement)) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }
    const container = windowElement.offsetParent;
    const containerRect = container?.getBoundingClientRect() ?? {
      left: 0,
      top: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    };
    const width = windowElement.getBoundingClientRect().width;
    const height = windowElement.getBoundingClientRect().height;
    return {
      minX: 0,
      maxX: Math.max(0, containerRect.width - width),
      minY: 0,
      maxY: Math.max(0, containerRect.height - height),
    };
  }

  private handleWindowPointerDown = (event: PointerEvent) => {
    this.beginDrag(event);
  };

  private getWindowPosition() {
    return this.style.zIndex || "0";
  }

  render() {
    const position =
      this.layout === "desktop"
        ? { left: `${this.x}px`, top: `${this.y}px` }
        : {};
    return html`<div
      id="window"
      data-layout=${this.layout}
      style=${styleMap({ ...position, zIndex: this.getWindowPosition() })}
    >
      <div id="topbar-wrapper">
        <div
          part="titlebar"
          id="draggable"
          data-draggable=${String(this.draggable && this.layout === "desktop")}
          data-dragging=${this.dragging ? "dragging" : "idle"}
          @pointerdown=${
            this.draggable && this.layout === "desktop"
              ? this.handleWindowPointerDown
              : undefined
          }
        >
          <a2k-window-topbar .dataState=${this.active ? "active" : "inactive"}
            >${this.heading}</a2k-window-topbar
          >
        </div>
        ${
          this.closeable
            ? html`<a2k-window-actions closeable></a2k-window-actions>`
            : null
        }
      </div>
      <slot name="toolbar"></slot>
      <div id="content">
        <a2k-stack><slot></slot></a2k-stack>
      </div>
      <slot name="statusbar"></slot>
    </div>`;
  }
}

export type { WindowLayout };
